import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, withPropsOnChange, branch, renderComponent, withHandlers } from 'recompose';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';
import { withProductListViewedEcommerceTracking, withSegmentContext } from '@sparefoot/react-core/segment';
import { withSegmentFacilityCardTracking, withExperiment } from '@sparefoot/react-core/decorators';

import { FacilityCard } from 'components/facility/FacilityCard';
import { FacilityRatingLabelWithReview } from 'components/facility/FacilityRatingLabel';
import { AdditionalFacilitiesBanner } from 'components/search/AdditionalFacilitiesBanner';
import { getFacilityPhoneNumber, getFacilityCardPhoneNumber } from 'store/modules/facilities';
import { getHigestReview } from 'store/modules/reviews';
import { getTollfreePhoneNumber } from 'store/modules/phone';

import NoResults from './NoResults';
import SearchError from './SearchError';

export const enhance = compose(
	withSegmentContext,
	connect(
		undefined,
		{
			getFacilityPhoneNumber,
			getHigestReview,
			getTollfreePhoneNumber,
			getFacilityCardPhoneNumber
		},
		mapDispatchToActions
	),
	withExperiment('sf_9564_ssdc_covid_dont_call_notification'),
	branch(({ error }) => !!error, renderComponent(SearchError)),
	branch(({ facilities }) => !facilities.length, renderComponent(NoResults)),
	withPropsOnChange(['searchDividerIndex'], ({ searchDividerIndex, location }) => {
		if (searchDividerIndex > 0) {
			return {
				injectedContent: {
					[searchDividerIndex - 1]: (
						<AdditionalFacilitiesBanner
							location={location}
						/>
					)
				}
			};
		}
		return null;
	}),
	withSegmentFacilityCardTracking,
	withHandlers({
		handleMouseOverRating: ({ actions }) => (facilityId) => actions.getHigestReview(facilityId)
	}),
	withProductListViewedEcommerceTracking(
		({ facilities }) => ({
			products: facilities.map((facility, index) => ({
				id: facility.id,
				name: facility.name,
				position: index + 1,
				rank: facility.position,
				inNetwork: facility.inNetwork,
				distance: facility.distance
			})),
			productIds: facilities.map((f) => f.id)
		})
	)
);

export function SearchResults({
	actions, baseUrl, cloudfrontUrl, facilities, isCLP, injectedContent,
	responsive, showRecommendedUnit, searchType, location,
	setFacilityRef, handleMouseOverRating, handleFacilityCardVisible,
	sf9564Variation // sf_9564_ssdc_covid_dont_call_notification
}) {
	return (
		<div className="search-results">
			{facilities.map((facility, index) => (
				<Fragment key={facility.id}>
					<FacilityCard
						baseUrl={baseUrl}
						cloudfrontUrl={cloudfrontUrl}
						facility={facility}
						getFacilityCardPhoneNumber={
							() => actions.getFacilityCardPhoneNumber(location.city, location.state, facility.id)
						}
						showRecommendedUnit={showRecommendedUnit}
						storageType={searchType}
						withUnits
						responsive={responsive}
						getRef={setFacilityRef}
						ratingRenderLabel={responsive === false ? undefined : FacilityRatingLabelWithReview}
						onMouseOverRating={handleMouseOverRating}
						lazyLoad
						onVisible={handleFacilityCardVisible}
						withSchema={isCLP}
						showHighCallVolumeMessaging={sf9564Variation === 'variation'}
					/>
					{injectedContent[index]}
				</Fragment>
			))}
		</div>
	);
}

SearchResults.propTypes = {
	facilities: PropTypes.arrayOf(
		PropTypes.object
	),
	actions: PropTypes.shape({
		getFacilityPhoneNumber: PropTypes.func.isRequired,
		getTollfreePhoneNumber: PropTypes.func.isRequired,
		getFacilityCardPhoneNumber: PropTypes.func.isRequired
	}).isRequired,
	location: PropTypes.object.isRequired,
	injectedContent: PropTypes.object,
	showRecommendedUnit: PropTypes.bool,
	responsive: PropTypes.bool,
	searchType: PropTypes.string,
	setFacilityRef: PropTypes.func,
	handleMouseOverRating: PropTypes.func,
	handleFacilityCardVisible: PropTypes.func.isRequired,
	isCLP: PropTypes.bool,
	baseUrl: PropTypes.string,
	cloudfrontUrl: PropTypes.string,
	sf9564Variation: PropTypes.string // sf_9564_ssdc_covid_dont_call_notification
};

SearchResults.defaultProps = {
	injectedContent: {},
	searchType: 'storage'
};

export default enhance(SearchResults);
